import i18next from 'i18next'
import { formatDistanceToNow, intlFormat, type Locale } from 'date-fns'
import languages from './languages.json'
import * as locales from 'date-fns/locale'

export type I18nInitOptions = {
    defaultLanguage?: string
}

export function initI18n(options?: I18nInitOptions) {
    return i18next.init({
        lng: options?.defaultLanguage ?? 'de-DE',
        fallbackLng: ['deDE'],

        interpolation: {
            escapeValue: false,
            format: (value, format, lng) => {
                // Get the dateFns locale for the current language

                let locale = locales.de
                const dateFnsName = i18next.getResource(
                    lng || 'de',
                    'translation',
                    'time.dateFnsName'
                )

                if (typeof dateFnsName === 'string' && dateFnsName in locales) {
                    locale = (locales as Record<string, Locale>)[dateFnsName]
                }

                // i18next does not provide a way to format dates relateve to now without predefined units

                switch (format) {
                    case 'relativetime':
                        return formatDistanceToNow(new Date(value), {
                            locale,
                            addSuffix: true,
                        })
                    case 'datetime':
                        try {
                            return intlFormat(new Date(value), {
                                locale: locale.code,
                            })
                        } catch (error) {
                            console.error(`locale ${locale.code} not found for datetime`)
                            throw error
                        }
                }

                return value
            },
        },
    })
}

export function getI18nLanguageInformation(): Record<
    keyof typeof languages,
    {
        name: string
        /**
         * ISO-639 set-1 language short names https://en.wikipedia.org/wiki/List_of_ISO_639_language_codes
         * */
        short: string
        englishName: string
        /**
         * iso-3166-2-alpha lower case https://en.wikipedia.org/wiki/ISO_3166-2
         * */
        countries?: string[]
    }
> {
    return languages
}

export default i18next
